import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import BottomNavBar from '../components/BottomNavBar';
import SEOMeta from '../components/SEOMeta';
import LoginPromptModal from '../components/LoginPromptModal';

const PRIMARY = '#2DBCAF';

interface MPASIGuideline {
  ageGroup: string;
  ageLabel: string;
  energyKcal: number;
  texture: string;
  frequency: string;
  portionStart: string;
  portionTarget: string;
  tips: string[];
}

const GUIDELINES: MPASIGuideline[] = [
  {
    ageGroup: '6-8m',
    ageLabel: '6-8 Bulan',
    energyKcal: 200,
    texture: 'Bubur kental, makanan yang dilumatkan',
    frequency: '2-3 kali makan utama + 1-2 kali selingan',
    portionStart: '2-3 sendok makan',
    portionTarget: '½ mangkok (125 ml)',
    tips: [
      'Mulai dengan 1 jenis makanan baru',
      'Tunggu 3 hari sebelum coba makanan baru',
      'Tekstur bertahap dari encer ke kental',
      'ASI tetap diberikan sesuai keinginan bayi',
    ],
  },
  {
    ageGroup: '9-11m',
    ageLabel: '9-11 Bulan',
    energyKcal: 300,
    texture: 'Makanan dicincang halus, finger food',
    frequency: '3-4 kali makan utama + 1-2 kali selingan',
    portionStart: '½ mangkok (125 ml)',
    portionTarget: '¾ mangkok (200 ml)',
    tips: [
      'Kenalkan finger food yang aman',
      'Variasikan jenis makanan',
      'Biarkan bayi belajar makan sendiri',
      'Tetap dampingi saat makan',
    ],
  },
  {
    ageGroup: '12-23m',
    ageLabel: '12-23 Bulan',
    energyKcal: 550,
    texture: 'Makanan keluarga, dicincang atau diiris',
    frequency: '3-4 kali makan utama + 1-2 kali selingan',
    portionStart: '¾ mangkok (200 ml)',
    portionTarget: '1 mangkok (250 ml)',
    tips: [
      'Anak bisa makan makanan keluarga',
      'Potong kecil-kecil untuk keamanan',
      'Ajak makan bersama keluarga',
      'Hindari makanan yang berisiko tersedak',
    ],
  },
];

const NON_ASI_GUIDELINE = {
  title: 'Jika Tidak Mendapat ASI (6-23 bulan)',
  notes: [
    'Tambahkan 1-2 gelas susu per hari (@250 ml)',
    'Tambahkan sumber cairan lain (air putih)',
    'Energi dari MPASI sesuai kelompok usia',
    'Pastikan asupan nutrisi tercukupi',
  ],
};

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #f6f8f8;
  font-family: 'Plus Jakarta Sans', sans-serif;
  padding-bottom: 100px;
`;

const Header = styled.header`
  background: white;
  padding: 16px 16px 14px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #64748b;
  .material-symbols-outlined { font-size: 24px; }
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  flex: 1;
`;

const ChildTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(45,188,175,0.1);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: ${PRIMARY};
`;

const AgeSelector = styled.div`
  display: flex;
  gap: 8px;
  padding: 16px;
  overflow-x: auto;
  &::-webkit-scrollbar { display: none; }
`;

const AgeBtn = styled.button<{ $active?: boolean }>`
  flex-shrink: 0;
  padding: 10px 18px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: 2px solid ${p => p.$active ? PRIMARY : '#e2e8f0'};
  background: ${p => p.$active ? PRIMARY : 'white'};
  color: ${p => p.$active ? 'white' : '#64748b'};
  transition: all 0.2s;
  &:hover { border-color: ${PRIMARY}; }
`;

const MainCard = styled.div`
  margin: 0 16px 16px;
  background: white;
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
`;

const StatBox = styled.div<{ $color: string }>`
  background: ${p => `${p.$color}10`};
  border-radius: 14px;
  padding: 14px;
  text-align: center;
  .icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${p => p.$color};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 8px;
    .material-symbols-outlined { font-size: 20px; color: white; }
  }
  .value { font-size: 18px; font-weight: 800; color: #0f172a; }
  .label { font-size: 11px; color: #64748b; margin-top: 2px; }
`;

const InfoSection = styled.div`
  margin-bottom: 16px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  .material-symbols-outlined { font-size: 16px; }
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: #0f172a;
  line-height: 1.5;
  background: #f8fafc;
  padding: 12px 14px;
  border-radius: 12px;
`;

const PortionBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8fafc;
  padding: 14px;
  border-radius: 12px;
`;

const PortionArrow = styled.div`
  color: ${PRIMARY};
  .material-symbols-outlined { font-size: 24px; }
`;

const PortionText = styled.div`
  flex: 1;
  .start { font-size: 13px; color: #64748b; }
  .target { font-size: 15px; font-weight: 700; color: #0f172a; margin-top: 2px; }
`;

const TipsCard = styled.div`
  margin: 0 16px 16px;
  background: #fef3c7;
  border-radius: 16px;
  padding: 16px;
`;

const TipsTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  .material-symbols-outlined { font-size: 18px; }
`;

const TipsList = styled.ul`
  margin: 0;
  padding: 0 0 0 18px;
  font-size: 13px;
  color: #78350f;
  line-height: 1.6;
  li { margin-bottom: 4px; }
`;

const NonASICard = styled.div`
  margin: 0 16px 16px;
  background: #eff6ff;
  border-radius: 16px;
  padding: 16px;
`;

const NonASITitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1e40af;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  .material-symbols-outlined { font-size: 18px; }
`;

const NonASIList = styled.ul`
  margin: 0;
  padding: 0 0 0 18px;
  font-size: 13px;
  color: #1e3a8a;
  line-height: 1.6;
  li { margin-bottom: 4px; }
`;

const ReferencesSection = styled.div`
  margin: 24px 16px 0;
  padding: 14px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const ReferencesTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  .material-symbols-outlined { font-size: 14px; }
`;

const ReferencesText = styled.p`
  font-size: 10px;
  color: #94a3b8;
  margin: 0;
  line-height: 1.5;
`;

function getAgeGroup(birthDate: string): string {
  const birth = new Date(birthDate);
  const now = new Date();
  const months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  
  if (months < 6) return 'under6m';
  if (months < 9) return '6-8m';
  if (months < 12) return '9-11m';
  if (months < 24) return '12-23m';
  return 'over24m';
}

export default function MPASIPage() {
  const navigate = useNavigate();
  const { user, activeChild } = useAuth();
  
  const childAgeGroup = activeChild ? getAgeGroup(activeChild.birthDate) : '6-8m';
  const defaultAgeGroup = ['6-8m', '9-11m', '12-23m'].includes(childAgeGroup) ? childAgeGroup : '6-8m';
  
  const [selectedAge, setSelectedAge] = useState(defaultAgeGroup);
  
  const guideline = GUIDELINES.find(g => g.ageGroup === selectedAge) || GUIDELINES[0];

  if (!user) {
    return (
      <>
        <SEOMeta
          title="Panduan MPASI - KinderGame.id"
          description="Panduan pemberian makan pendamping ASI sesuai usia anak"
          path="/tumbuh-kembang/mpasi"
        />
        <PageWrapper>
          <Header>
            <HeaderTop>
              <BackBtn onClick={() => navigate('/tumbuh-kembang')}>
                <span className="material-symbols-outlined">arrow_back</span>
              </BackBtn>
              <HeaderTitle>Panduan MPASI</HeaderTitle>
            </HeaderTop>
          </Header>
        </PageWrapper>
        <BottomNavBar />
        <LoginPromptModal
          title="Ups! Kamu Belum Login"
          subtitle="Login untuk melihat panduan MPASI"
          onClose={() => navigate('/tumbuh-kembang')}
        />
      </>
    );
  }

  return (
    <>
      <SEOMeta
        title="Panduan MPASI - KinderGame.id"
        description="Panduan pemberian makan pendamping ASI sesuai usia anak"
        path="/tumbuh-kembang/mpasi"
      />
      <PageWrapper>
        <Header>
          <HeaderTop>
            <BackBtn onClick={() => navigate('/tumbuh-kembang')}>
              <span className="material-symbols-outlined">arrow_back</span>
            </BackBtn>
            <HeaderTitle>Panduan MPASI</HeaderTitle>
            {activeChild && (
              <ChildTag>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>face</span>
                {activeChild.name}
              </ChildTag>
            )}
          </HeaderTop>
        </Header>

        <AgeSelector>
          {GUIDELINES.map(g => (
            <AgeBtn
              key={g.ageGroup}
              $active={selectedAge === g.ageGroup}
              onClick={() => setSelectedAge(g.ageGroup)}
            >
              {g.ageLabel}
            </AgeBtn>
          ))}
        </AgeSelector>

        <MainCard>
          <CardTitle>
            🍼 Panduan Usia {guideline.ageLabel}
          </CardTitle>

          <StatGrid>
            <StatBox $color="#F59E0B">
              <div className="icon">
                <span className="material-symbols-outlined">local_fire_department</span>
              </div>
              <div className="value">{guideline.energyKcal}</div>
              <div className="label">kkal/hari</div>
            </StatBox>
            <StatBox $color="#3B82F6">
              <div className="icon">
                <span className="material-symbols-outlined">schedule</span>
              </div>
              <div className="value">{guideline.frequency.split(' ')[0]}</div>
              <div className="label">kali makan</div>
            </StatBox>
          </StatGrid>

          <InfoSection>
            <InfoLabel>
              <span className="material-symbols-outlined">restaurant</span>
              Tekstur Makanan
            </InfoLabel>
            <InfoValue>{guideline.texture}</InfoValue>
          </InfoSection>

          <InfoSection>
            <InfoLabel>
              <span className="material-symbols-outlined">schedule</span>
              Frekuensi Makan
            </InfoLabel>
            <InfoValue>{guideline.frequency}</InfoValue>
          </InfoSection>

          <InfoSection>
            <InfoLabel>
              <span className="material-symbols-outlined">measuring_tape</span>
              Porsi per Makan
            </InfoLabel>
            <PortionBox>
              <PortionText>
                <div className="start">Mulai: {guideline.portionStart}</div>
                <div className="target">Target: {guideline.portionTarget}</div>
              </PortionText>
              <PortionArrow>
                <span className="material-symbols-outlined">trending_up</span>
              </PortionArrow>
            </PortionBox>
          </InfoSection>
        </MainCard>

        <TipsCard>
          <TipsTitle>
            <span className="material-symbols-outlined">lightbulb</span>
            Tips Pemberian MPASI
          </TipsTitle>
          <TipsList>
            {guideline.tips.map((tip, i) => (
              <li key={i}>{tip}</li>
            ))}
          </TipsList>
        </TipsCard>

        <NonASICard>
          <NonASITitle>
            <span className="material-symbols-outlined">info</span>
            {NON_ASI_GUIDELINE.title}
          </NonASITitle>
          <NonASIList>
            {NON_ASI_GUIDELINE.notes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </NonASIList>
        </NonASICard>

        <ReferencesSection>
          <ReferencesTitle>
            <span className="material-symbols-outlined">menu_book</span>
            Referensi
          </ReferencesTitle>
          <ReferencesText>
            WHO/PAHO 2003 • WHO 2009 • WHO 2010 • UNICEF 2013 • Pedoman MPASI Kemenkes RI
          </ReferencesText>
        </ReferencesSection>
      </PageWrapper>

      <BottomNavBar />
    </>
  );
}
